import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { logoutRedirect } from "src/async/fetch";
import { AuthContext } from "src/context/auth";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { getNavigateLink } from "src/helpers/userHelper";
import useSocketListeners from "src/hooks/useSocketListeners";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import Dashboardlayout from "src/layouts/DashboardLayout";
import TravelLayout from "src/layouts/TravelLayout";
import TravelLayout2 from "src/layouts/TravelLayout2";

const ProtectedRouteInner = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { user, setUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { workspace_id } = useParams();
  const { page, form, document } = useUrlQueryParams();
  const { startSwitching } = useWorkspaceSwitching();

  const navigate = useNavigate();
  const { header_page_id } = useParams();

  useEffect(() => {
    if (!isLoaded) {
      startSwitching();
    }
  }, [isLoaded, startSwitching]);

  const handleGetHeaderMenu = async () => {
    try {
      const { data } = await apiRequest("GET", "/portal/users/workspaces/header-menu");
      navigate(`workspace/${data?.data?.workspace}/menu/page/${header_page_id}`);
    } catch (error) {
      try {
        const { data: response } = await apiRequest("GET", "/portal/users/workspaces/page", {
          params: { header_page_id },
        });

        const navigate_link = getNavigateLink(response);

        navigate(navigate_link);
      } catch (error) {
        navigate("/permission-denied");
      }
    }
  };

  useEffect(() => {
    if (header_page_id) {
      handleGetHeaderMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header_page_id]);

  const loadUser = async () => {
    if (user?._id) {
      setIsLoaded(true);
      return;
    }

    try {
      const res = await apiRequest(
        "post",
        "/portal/users/self/details",
        {
          body: { token: localStorage.getItem("accessToken"), access_token: localStorage.getItem("accessToken") },
        },
        { returnErrorWithstatus: true }
      );

      const resData = res?.data;

      if (resData?.status === "client_inactive" || resData?.status === "inactive" || resData?.status === 403) {
        navigate("/client-inactive");
        return;
      }

      if (resData?.status !== 200) logoutRedirect();
      setUser(resData?.data);
    } catch (error) {
      if (
        error?.response?.data?.status === "client_inactive" ||
        error?.response?.data?.status === "inactive" ||
        error?.response?.status === 403 ||
        error?.data?.status === "client_inactive" ||
        error?.status === 403
      ) {
        navigate("/client-inactive");
        return;
      }
      if (error?.message !== "Failed to fetch" && error?.message !== "The user aborted a request.") logoutRedirect();
    } finally {
      setIsLoaded(true);
    }
  };

  const getPublicGroupDetails = async () => {
    try {
      const { data: resData } = await apiRequest(
        "get",
        "/portal/workspaces/:workspace_id/groups/:group_id/public",
        {
          params: { workspace_id, group_id: searchParams.get("group") },
          queries: { page, form, document },
        },
        { returnErrorWithstatus: true },
      );

      if (resData?.status !== 200) {
        loadUser();
      }
    } catch (error) {
      if (error.message !== "canceled") {
        if (error.message !== "Failed to fetch" && error.message !== "The user aborted a request.") loadUser();
      }
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("accessToken")) loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useSocketListeners([
    {
      event: "profile-updated-response",
      title: "Profile updated",
      message: "Your profile has been updated, sync now",
      handler: async () => {
        const { data: resData } = await apiRequest("post", "/portal/users/self/details", {
          body: { token: localStorage.getItem("accessToken"), access_token: localStorage.getItem("accessToken") },
        });
        return resData.data;
      },
      onSync: (updatedUser) => {
        setUser(updatedUser);
      },
    },
  ]);

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      if (location?.pathname?.includes("/embed") && workspace_id && searchParams.get("group")) {
        getPublicGroupDetails();
      } else {
        logoutRedirect();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, location?.pathname, workspace_id, searchParams]);

  if (window.location.pathname.includes("/embeded/") || window.location.pathname.includes("/embed/items/")) {
    return <Dashboardlayout nested />;
  }

  if (window.location.pathname === "/travel/dashboard") {
    return <TravelLayout />;
  }

  if (["/travel/home", "/travel/help", "/travel/profile", "/travel/greener"].includes(window.location.pathname)) {
    return <TravelLayout2 />;
  }

  return <Dashboardlayout />;
};

const ProtectedRoute = () => {
  return <ProtectedRouteInner />;
};

export default ProtectedRoute;
